import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from '@ibk/shared-translation';
import { Icon, Loading } from '@ibk/shared-ui';
import { useEmissorProductCatalog } from '../../../../hooks/useEmissorProductCatalog';
import { isInlineSvgIcon } from '../../../../utils/isInlineSvgIcon';
import styles from './InsideSalesProductPortfolioSection.module.css';
import { buildPortfolioCardTestId } from './portfolioCardTestId';
import { easeInOutCubic, mapCardScrollPositions, readTrackInset, resolvePortfolioScrollTarget, SCROLL_ANIMATION_DURATION, } from './portfolioCarousel';
function ProductIcon({ icon, testId }) {
    if (!icon) {
        return null;
    }
    if (isInlineSvgIcon(icon)) {
        return (_jsx("span", { className: styles.cardIcon, "aria-hidden": true, "data-testid": `iconInsideSalesPortfolio${testId}`, dangerouslySetInnerHTML: { __html: icon } }));
    }
    return _jsx("img", { className: styles.cardIcon, src: icon, alt: '', "data-testid": `iconInsideSalesPortfolio${testId}` });
}
export function InsideSalesProductPortfolioSection() {
    const { t } = useTranslation();
    const { products, isLoading, isError } = useEmissorProductCatalog();
    const trackRef = useRef(null);
    const animationFrameRef = useRef(null);
    const [canScrollPrev, setCanScrollPrev] = useState(false);
    const [canScrollNext, setCanScrollNext] = useState(false);
    const updateScrollState = useCallback(() => {
        const track = trackRef.current;
        if (!track) {
            return;
        }
        const { scrollLeft, scrollWidth, clientWidth } = track;
        setCanScrollPrev(scrollLeft > 1);
        setCanScrollNext(scrollLeft + clientWidth < scrollWidth - 1);
    }, []);
    useEffect(() => {
        updateScrollState();
        const track = trackRef.current;
        if (!track) {
            return undefined;
        }
        track.addEventListener('scroll', updateScrollState, { passive: true });
        window.addEventListener('resize', updateScrollState);
        return () => {
            track.removeEventListener('scroll', updateScrollState);
            window.removeEventListener('resize', updateScrollState);
        };
    }, [updateScrollState, products, isLoading]);
    useEffect(() => () => {
        if (animationFrameRef.current !== null) {
            cancelAnimationFrame(animationFrameRef.current);
        }
    }, []);
    const handleScroll = useCallback((direction) => {
        if (animationFrameRef.current !== null) {
            return;
        }
        const track = trackRef.current;
        if (!track) {
            return;
        }
        const cards = Array.from(track.children);
        if (cards.length === 0) {
            return;
        }
        const inset = readTrackInset(getComputedStyle(track).paddingLeft);
        const current = track.scrollLeft;
        const maxScroll = track.scrollWidth - track.clientWidth;
        const trackLeft = track.getBoundingClientRect().left;
        const cardPositions = mapCardScrollPositions(cards.map(card => card.getBoundingClientRect().left), trackLeft, inset, current, maxScroll);
        const target = resolvePortfolioScrollTarget(direction, current, maxScroll, cardPositions);
        const distance = target - current;
        if (Math.abs(distance) < 1) {
            return;
        }
        const startTime = performance.now();
        const animate = (now) => {
            const progress = Math.min((now - startTime) / SCROLL_ANIMATION_DURATION, 1);
            track.scrollLeft = current + distance * easeInOutCubic(progress);
            if (progress < 1) {
                animationFrameRef.current = requestAnimationFrame(animate);
            }
            else {
                animationFrameRef.current = null;
            }
        };
        animationFrameRef.current = requestAnimationFrame(animate);
    }, []);
    const renderProductCard = (product) => {
        const testId = buildPortfolioCardTestId(product.code, product.id);
        return (_jsxs("article", { className: styles.card, "data-testid": `viewInsideSalesPortfolioCard${testId}`, children: [product.isPat ? (_jsx("span", { className: styles.patTag, "data-testid": `tagInsideSalesPortfolioPat${testId}`, children: t('benefits.inside-sales.portfolio.tag.pat') })) : null, _jsxs("div", { className: styles.cardHeader, children: [_jsx(ProductIcon, { icon: product.icon, testId: testId }), _jsx("h3", { className: styles.cardTitle, children: product.name })] }), _jsx("p", { className: styles.cardDescription, children: product.description })] }, product.id));
    };
    const renderViewportContent = () => {
        if (isLoading) {
            return (_jsx("div", { className: styles.status, "data-testid": 'viewInsideSalesPortfolioLoading', children: _jsx(Loading, { size: 'small' }) }));
        }
        if (isError) {
            return (_jsx("p", { className: styles.statusMessage, "data-testid": 'textInsideSalesPortfolioError', children: t('benefits.inside-sales.portfolio.error') }));
        }
        if (products.length === 0) {
            return (_jsx("p", { className: styles.statusMessage, "data-testid": 'textInsideSalesPortfolioEmpty', children: t('benefits.inside-sales.portfolio.empty') }));
        }
        return (_jsx("div", { className: styles.track, ref: trackRef, children: products.map(renderProductCard) }));
    };
    const hasCards = !isLoading && !isError && products.length > 0;
    return (_jsx("section", { className: styles.section, "data-testid": 'viewInsideSalesProductPortfolio', children: _jsxs("div", { className: styles.box, children: [_jsxs("header", { className: styles.header, children: [_jsxs("h2", { className: styles.title, children: [t('benefits.inside-sales.portfolio.title.lead'), _jsx("span", { className: styles.highlight, children: t('benefits.inside-sales.portfolio.title.highlight') })] }), _jsx("p", { className: styles.subtitle, children: t('benefits.inside-sales.portfolio.subtitle') })] }), _jsx("div", { className: styles.viewport, children: renderViewportContent() }), _jsxs("div", { className: styles.nav, children: [_jsx("button", { type: 'button', className: styles.navButton, "data-testid": 'buttonInsideSalesPortfolioPrev', "aria-label": t('benefits.inside-sales.portfolio.nav.prev'), onClick: () => handleScroll(-1), disabled: !hasCards || !canScrollPrev, children: _jsx(Icon, { name: 'arrowChevronLeft', variant: 'mask', tintColor: hasCards && canScrollPrev ? 'var(--brand-tr-orange)' : 'var(--stroke-neutral-silver)', alt: '', "aria-hidden": true, className: styles.navIcon }) }), _jsx("button", { type: 'button', className: styles.navButton, "data-testid": 'buttonInsideSalesPortfolioNext', "aria-label": t('benefits.inside-sales.portfolio.nav.next'), onClick: () => handleScroll(1), disabled: !hasCards || !canScrollNext, children: _jsx(Icon, { name: 'arrowChevronRight', variant: 'mask', tintColor: hasCards && canScrollNext ? 'var(--brand-tr-orange)' : 'var(--stroke-neutral-silver)', alt: '', "aria-hidden": true, className: styles.navIcon }) })] })] }) }));
}
